import { createContext, useContext, type ReactElement } from 'react'
import { Image as ImageIcon } from 'lucide-react'
import { StoryRow, RankedList } from '../page-blocks/implementations'
import type { PageBlockType, WireArticle } from '../../api/types'
import { PAGE_BLOCK_REGISTRY } from '../page-blocks/registry'
import { PLACEHOLDER_ARTICLES } from './placeholder-data'

// Articles provided to preview sub-components via context.
// Defaults to PLACEHOLDER_ARTICLES so callers that don't pass articles
// still see a realistic structural preview.
const PreviewArticlesContext = createContext<WireArticle[]>(PLACEHOLDER_ARTICLES)
function usePreviewArticles() { return useContext(PreviewArticlesContext) }

function formatDate(iso: string | undefined): string {
  if (!iso) return ''
  try {
    return new Date(iso).toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch { return iso }
}

function PlaceholderImage({ className }: { className?: string }) {
  return (
    <div className={`flex items-center justify-center bg-fill ${className ?? ''}`}>
      <ImageIcon size={32} className="text-fg-secondary" aria-hidden="true" />
    </div>
  )
}

interface BlockPreviewProps {
  type: PageBlockType
  config?: Record<string, string | number | boolean | null>
  compact?: boolean
  /** Real articles to show in the preview. Padded with placeholders when fewer than the block's max. */
  articles?: WireArticle[]
}

export function BlockPreview({ type, config, compact = false, articles }: BlockPreviewProps) {
  const resolvedConfig = config ?? PAGE_BLOCK_REGISTRY[type].defaultConfig
  // Pad real articles with placeholders so every block type always has enough entries
  const articlePool: WireArticle[] = articles && articles.length > 0
    ? [...articles, ...PLACEHOLDER_ARTICLES].slice(0, 12)
    : PLACEHOLDER_ARTICLES

  let content: ReactElement | null
  switch (type) {
    case 'story-row': {
      const rawCount = Number(resolvedConfig.columnCount ?? 3)
      const columnCount = ([2, 3, 4, 5].includes(rawCount) ? rawCount : 3) as 2 | 3 | 4 | 5
      content = (
        <StoryRow
          articles={articlePool.slice(0, columnCount).map(a => ({ ...a, thumbnail: a.id.startsWith('ph-') ? undefined : a.thumbnail }))}
          config={{ columnCount, sectionHeaderEnabled: false, showImages: true, showSummary: false }}
        />
      )
      break
    }
    case 'ranked-list':
      content = (
        <RankedList
          articles={articlePool.slice(0, 5)}
          config={{
            sectionHeaderEnabled: false,
            numberingStyle: 'numeric',
            showSummary: false,
            showFooterAction: true,
            footerActionLabel: 'Explore More',
          }}
        />
      )
      break
    case 'unranked-list':
      content = <UnrankedListPreview />
      break
    case 'inline-feature':
      content = <InlineFeaturePreview compact={compact} />
      break
    case 'feature-with-rail':
      content = <FeatureWithRailPreview />
      break
    case 'hero-overlay':
      content = <HeroOverlayPreview />
      break
    case 'editorial-grid':
      content = <EditorialGridPreview config={resolvedConfig} />
      break
    case 'livewire-callout':
      content = <LiveWireCalloutPreview />
      break
    case 'section-divider':
      content = <SectionDividerPreview config={resolvedConfig} />
      break
    case 'spacer':
      content = <SpacerPreview config={resolvedConfig} />
      break
    default:
      content = null
  }
  return (
    <PreviewArticlesContext.Provider value={articlePool}>
      {content}
    </PreviewArticlesContext.Provider>
  )
}

function UnrankedListPreview() {
  const articles = usePreviewArticles().slice(0, 5)
  return (
    <div>
      {articles.map((article, i) => (
        <div key={i} className="px-3 py-2.5">
          <div className="font-zodiak text-xs font-bold leading-snug text-fg">
            ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}
          </div>
          <div className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">
            {formatDate(article.publishedAt)} · {article.regions?.[0]}
          </div>
        </div>
      ))}
    </div>
  )
}

function InlineFeaturePreview({ compact = false }: { compact?: boolean }) {
  const article = usePreviewArticles()[0]
  return (
    <div className={`grid ${compact ? 'grid-cols-[1fr_1fr]' : 'grid-cols-[2fr_3fr]'}`}>
      {/* Text column */}
      <div className="flex min-h-[180px] flex-col p-4">
        <h3 className={`font-zodiak font-bold leading-snug text-fg ${compact ? 'text-xs' : 'text-xl'}`}>
          ({[article.classification, ...(article.disseminationControls ?? [])].join('//')}) {article.title}
        </h3>
        <p className={`mt-2 leading-4 text-fg-secondary line-clamp-3 ${compact ? 'text-[9px]' : 'text-[10px]'}`}>{article.description}</p>
        <div className="mt-auto pt-3">
          <div className="text-[9px] font-semibold uppercase tracking-widest text-fg-secondary">{article.regions?.[0]}</div>
          <div className="mt-0.5 flex items-center gap-1.5 text-[9px] uppercase tracking-wide text-fg-secondary">
            {formatDate(article.publishedAt)}
            <span aria-hidden="true">·</span>
            <span>&#x1F4F7;</span>
            <span>{article.thumbnail ? [article.thumbnail.classification, ...(article.thumbnail.disseminationControls ?? [])].join('//') : [article.classification, ...(article.disseminationControls ?? [])].join('//')}</span>
          </div>
        </div>
      </div>
      {/* Image column */}
      <div className="min-h-[180px]">
        <PlaceholderImage className="h-full w-full" />
      </div>
    </div>
  )
}

function FeatureWithRailPreview() {
  const pool = usePreviewArticles()
  const lead = pool[0]
  const supporting = pool.slice(1, 4)
  return (
    <div className="grid grid-cols-[2fr_1fr_1fr] divide-x divide-line">
      <div className="p-5">
        <PlaceholderImage className="mb-3 aspect-video w-full" />
        <h3 className="font-zodiak text-xl font-bold leading-tight text-fg">
          ({[lead.classification, ...(lead.disseminationControls ?? [])].join('//')}) {lead.title}
        </h3>
        <p className="mt-2 text-sm leading-6 text-fg-secondary">{lead.description}</p>
        <div className="mt-3 text-[10px] uppercase tracking-wide text-fg-secondary">{formatDate(lead.publishedAt)}</div>
      </div>
      <div className="col-span-2 divide-y divide-line">
        {supporting.map((art, i) => (
          <div key={i} className="flex gap-3 p-3">
            <PlaceholderImage className="h-16 w-16 shrink-0" />
            <div>
              <div className="font-zodiak text-xs font-bold leading-snug text-fg">
                ({[art.classification, ...(art.disseminationControls ?? [])].join('//')}) {art.title}
              </div>
              <div className="mt-1 text-[9px] uppercase tracking-wide text-fg-secondary">{art.regions?.[0]}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function HeroOverlayPreview() {
  const pool = usePreviewArticles()
  const lead = pool[0]
  const supporting = pool.slice(1, 3)
  return (
    <div className="relative overflow-hidden" style={{ minHeight: '260px' }}>
      <div className="absolute inset-0 flex items-center justify-center bg-fill">
        <ImageIcon size={48} className="text-fg-secondary" aria-hidden="true" />
      </div>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative grid h-full grid-cols-[3fr_1fr]" style={{ minHeight: '260px' }}>
        <div className="flex flex-col justify-end p-6">
          <div className="mb-2">
            <span className="bg-red-9 px-2 py-0.5 text-[9px] font-bold uppercase tracking-widest text-white">
              Top News
            </span>
          </div>
          <h3 className="font-zodiak text-2xl font-bold leading-tight text-white">
            ({[lead.classification, ...(lead.disseminationControls ?? [])].join('//')}) {lead.title}
          </h3>
          <p className="mt-2 text-sm text-white/70">{lead.description}</p>
          <div className="mt-3 text-[9px] uppercase tracking-wide text-white/50">
            {lead.regions?.[0]} · {formatDate(lead.publishedAt)}
          </div>
        </div>
        <div className="flex flex-col justify-center gap-4 border-l border-white/20 bg-black/40 p-4">
          {supporting.map((art, i) => (
            <div key={i} className="border-b border-white/20 pb-3 last:border-0 last:pb-0">
              <div className="mb-1 text-[9px] font-semibold uppercase tracking-wider text-white/50">
                {art.regions?.[0]}
              </div>
              <div className="font-zodiak text-xs font-bold leading-snug text-white">
                ({[art.classification, ...(art.disseminationControls ?? [])].join('//')}) {art.title}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

function EditorialGridPreview({ config }: { config?: Record<string, string | number | boolean | null> }) {
  const preset = (config?.layoutPreset as string) ?? '3-equal'
  // Derive column count from preset name prefix
  const cols = preset.startsWith('2-') ? 2 : 3
  const labels = ['Column 1', 'Column 2', 'Column 3'].slice(0, cols)
  const gridClass = cols === 2 ? 'grid-cols-2' : 'grid-cols-3'
  return (
    <div className={`grid ${gridClass} divide-x divide-line`}>
      {labels.map((label, i) => (
        <div key={i} className="flex flex-col items-center justify-center gap-2 p-6 text-center">
          <div className="flex h-8 w-8 items-center justify-center rounded-full border-2 border-dashed border-line text-xs font-bold text-fg-secondary">
            {i + 1}
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-fg-secondary">{label}</div>
          <div className="text-[9px] text-fg-secondary opacity-60">column-compatible block</div>
        </div>
      ))}
    </div>
  )
}

function LiveWireCalloutPreview() {
  return (
    <div className="mx-4 my-3 flex items-center gap-4 rounded border border-line bg-fill px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-solid text-sm text-white">
        ▶
      </div>
      <div className="flex-1">
        <div className="text-[9px] font-bold uppercase tracking-widest text-fg-secondary">Live Now</div>
        <div className="text-sm font-semibold text-fg">Come to today's Wire KeLAY</div>
      </div>
      <div className="text-xs text-fg-secondary">24 min</div>
    </div>
  )
}

function SectionDividerPreview({ config }: { config?: Record<string, string | number | boolean | null> }) {
  const style = (config?.style as string) ?? 'plain'
  const label = (config?.label as string | null) ?? null
  return (
    <div className="flex items-center gap-3 px-6 py-5">
      {style === 'labeled' && label ? (
        <>
          <div className="flex-1 border-t border-line" />
          <span className="shrink-0 text-[10px] font-bold uppercase tracking-widest text-fg-secondary">{label}</span>
          <div className="flex-1 border-t border-line" />
        </>
      ) : (
        <div
          className={`w-full border-t ${style === 'thick' ? 'border-t-2 border-fg-secondary opacity-40' : 'border-line'}`}
        />
      )}
    </div>
  )
}

const SPACER_HEIGHTS: Record<string, string> = { sm: '8px', md: '24px', lg: '48px', xl: '80px' }

function SpacerPreview({ config }: { config?: Record<string, string | number | boolean | null> }) {
  const size = (config?.size as string) ?? 'md'
  const height = SPACER_HEIGHTS[size] ?? SPACER_HEIGHTS.md
  return (
    <div
      className="mx-6 my-2 flex items-center justify-center rounded border border-dashed border-line"
      style={{ minHeight: '24px', height }}
    >
      <span className="text-[9px] font-semibold uppercase tracking-widest text-fg-secondary opacity-50">
        {size.toUpperCase()} · {height}
      </span>
    </div>
  )
}
